/**
 * Bundle Discount Code Generator
 * Automatically creates and applies discount codes for bundles
 */

class BundleDiscountAPI {
  constructor(config) {
    this.apiToken = config.apiToken;
    this.shopDomain = config.shopDomain;
    this.apiVersion = '2026-01';
    
    // Storefront API config for applying multiple discount codes
    this.storefrontAccessToken = config.storefrontAccessToken || 'fd0e2c1c25a2619c0331e759eb5381c5';
    // Use stable API version for production (2026-01 is unstable/future)
    this.storefrontApiVersion = config.storefrontApiVersion || '2025-01';
    this.storefrontApiUrl = `https://${this.shopDomain}/api/${this.storefrontApiVersion}/graphql.json`;
    
    // Cache for Storefront cart ID
    this._storefrontCartId = null;
    this._lastAjaxCartToken = null;
  }

  /**
   * Clear cached Storefront cart (call when Ajax cart changes)
   */
  clearStorefrontCart() {
    this._storefrontCartId = null;
    this._lastAjaxCartToken = null;
    try {
      sessionStorage.removeItem('bundle_storefront_cart_id');
      sessionStorage.removeItem('bundle_storefront_checkout_url');
    } catch (e) {
      // Storage not available
    }
  }

  /**
   * Read the Ajax cart through the shared cart.js cache when available.
   * Falls back to a direct cart request on pages without the cart controller.
   */
  async getAjaxCart(cachedCart = null, forceRefresh = false) {
    if (cachedCart) {
      return cachedCart;
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.fetchCartWithCache === 'function') {
      return cartItems.fetchCartWithCache(forceRefresh);
    }

    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('Failed to fetch cart');
    }

    return cartResponse.json();
  }

  /**
   * Execute a Storefront API GraphQL query
   */
  async storefrontQuery(query, variables = {}) {
    const response = await fetch(this.storefrontApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
      },
      body: JSON.stringify({ query, variables })
    });
    
    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }
    
    return response.json();
  }

  /**
   * Create a Storefront API cart from current Ajax cart items
   * Includes buyerIdentity to get prices in buyer's presentment currency
   */
  async createStorefrontCart(cachedCart = null) {
    
    // Fetch current Ajax cart
    const cart = await this.getAjaxCart(cachedCart, true);
    
    if (!cart.items || cart.items.length === 0) {
      throw new Error('Cart is empty');
    }
    
    // Build line items for Storefront cart
    const lines = cart.items.map(item => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
      attributes: item.properties 
        ? Object.entries(item.properties).map(([key, value]) => ({ key, value: String(value) }))
        : []
    }));
    
    // Get buyer's country code for presentment currency
    // Priority: Shopify.country > cart.currency mapping > default
    const buyerCountry = this.getBuyerCountryCode();
    
    // Build cart input with buyer identity for correct presentment currency
    const cartInput = {
      lines,
      buyerIdentity: buyerCountry ? { countryCode: buyerCountry } : undefined
    };
    
    const mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            discountCodes {
              code
              applicable
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    
    const result = await this.storefrontQuery(mutation, { input: cartInput });
    
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Cart creation failed');
      }
      
      if (result.data?.cartCreate?.userErrors?.length > 0) {
        throw new Error(result.data.cartCreate.userErrors[0]?.message || 'Cart creation failed');
      }
    
    const storefrontCart = result.data?.cartCreate?.cart;
    if (!storefrontCart) {
      throw new Error('No cart returned from Storefront API');
    }
    
    this._storefrontCartId = storefrontCart.id;
    
    // Persist cart ID in sessionStorage for page navigation
    try {
      sessionStorage.setItem('bundle_storefront_cart_id', storefrontCart.id);
      sessionStorage.setItem('bundle_storefront_checkout_url', storefrontCart.checkoutUrl);
    } catch (e) {
      // Storage not available
    }
    
    return storefrontCart;
  }

  /**
   * Get buyer's country code for Storefront API buyerIdentity
   * This ensures prices are returned in the correct presentment currency
   */
  getBuyerCountryCode() {
    // 1. Try Shopify.country (set by Shopify Markets)
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country;
    }
    
    // 2. Try to get from localization data
    if (typeof Shopify !== 'undefined' && Shopify.locale) {
      // Extract country from locale like 'en-BE' -> 'BE'
      const parts = Shopify.locale.split('-');
      if (parts.length > 1 && parts[1].length === 2) {
        return parts[1].toUpperCase();
      }
    }
    
    // 3. Map currency to common country codes
    const currencyToCountry = {
      'EUR': 'BE',  // Belgium as default for EUR (European market)
      'USD': 'US',
      'GBP': 'GB',
      'CAD': 'CA',
      'AUD': 'AU',
      'JPY': 'JP',
      'CNY': 'CN',
      'SGD': 'SG',
      'HKD': 'HK',
      'TWD': 'TW',
      'KRW': 'KR'
    };
    
    if (typeof Shopify !== 'undefined' && Shopify.currency?.active) {
      const country = currencyToCountry[Shopify.currency.active];
      if (country) return country;
    }
    
    // 4. Default to null (will use store's default market)
    return null;
  }

  /**
   * Re-fetch Storefront cart to get updated discount allocations
   * Workaround for Storefront API timing issue where allocations can be stale
   */
  async refetchStorefrontCart(cartId) {
    const query = `
      query cartQuery($cartId: ID!) {
        cart(id: $cartId) {
          id
          checkoutUrl
          discountCodes {
            code
            applicable
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
          }
          lines(first: 50) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    product {
                      title
                    }
                  }
                }
                discountAllocations {
                  discountedAmount {
                    amount
                    currencyCode
                  }
                }
                cost {
                  amountPerQuantity {
                    amount
                    currencyCode
                  }
                  compareAtAmountPerQuantity {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
            totalTaxAmount {
              amount
              currencyCode
            }
          }
        }
      }
    `;
    
    try {
      const result = await this.storefrontQuery(query, { cartId });
      
      if (result.errors) {
        return null;
      }
      
      return result.data?.cart || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Apply multiple discount codes via Storefront API cartDiscountCodesUpdate
   * This is the only way to apply multiple combinable discount codes
   * @param {Array} codes - Discount codes to apply
   * @param {Object} cachedCart - Optional cached cart object to avoid redundant fetch
   */
  async applyDiscountCodesViaStorefront(codes = [], cachedCart = null) {
    const codeList = Array.isArray(codes) ? codes : [codes];
    // Force uppercase for deduplication - Shopify discount codes are case-insensitive
    const normalized = Array.from(new Set(
      codeList
        .map(code => (code || '').toString().trim().toUpperCase())
        .filter(Boolean)
    ));
    
    if (normalized.length === 0) {
      return null;
    }
    
    try {
      // Use cached cart if provided, otherwise fetch
      const ajaxCart = await this.getAjaxCart(cachedCart, true);
      const currentAjaxToken = ajaxCart?.token;
      const currentItemCount = ajaxCart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      
      // Force new Storefront cart if Ajax cart has changed
      const ajaxCartChanged = this._lastAjaxCartToken !== currentAjaxToken ||
                              this._lastAjaxCartItemCount !== currentItemCount;
      
      if (ajaxCartChanged && this._storefrontCartId) {
        // Clear the old Storefront cart since Ajax cart has changed
        this._storefrontCartId = null;
      }
      
      // Track Ajax cart state
      this._lastAjaxCartToken = currentAjaxToken;
      this._lastAjaxCartItemCount = currentItemCount;
      
      // Create Storefront cart if we don't have one
      let storefrontCart;
      if (!this._storefrontCartId) {
        storefrontCart = await this.createStorefrontCart(ajaxCart);
      }
      
      const cartId = this._storefrontCartId;
      
      // Apply discount codes
      // Query both cart-level and line-level discount allocations
      // 2026-01 API: discountCodes is now required (breaking change)
      const mutation = `
        mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
            cart {
              id
              checkoutUrl
              discountCodes {
                code
                applicable
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 50) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          title
                        }
                      }
                    }
                    discountAllocations {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                    cost {
                      amountPerQuantity {
                        amount
                        currencyCode
                      }
                      compareAtAmountPerQuantity {
                        amount
                        currencyCode
                      }
                      totalAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;
      
      const result = await this.storefrontQuery(mutation, {
        cartId,
        discountCodes: normalized
      });
      
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Discount application failed');
      }
      
      let updatedCart = result.data?.cartDiscountCodesUpdate?.cart;
      const userErrors = result.data?.cartDiscountCodesUpdate?.userErrors || [];
      
      // userErrors are handled silently - codes may already be applied or have other issues
      
      if (!updatedCart) {
        throw new Error('No cart returned from discount update');
      }
      
      // WORKAROUND: Re-fetch cart after delay to get accurate discount allocations
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Re-fetch cart with fresh discount data
      const refetchedCart = await this.refetchStorefrontCart(cartId);
      if (refetchedCart) {
        updatedCart = refetchedCart;
      }
      
      // Check which codes were applied
      const discountCodeDetails = (updatedCart.discountCodes || []).map(dc => ({
        code: dc.code,
        applicable: dc.applicable
      }));
      const appliedCodes = discountCodeDetails
        .filter(dc => dc.applicable)
        .map(dc => dc.code);
      const notApplied = normalized.filter(code => 
        !appliedCodes.some(ac => ac.toUpperCase() === code.toUpperCase())
      );
      
      // Check line-level discount allocations
      const lines = updatedCart.lines?.edges || [];
      const lineAllocations = lines.map(edge => {
        const node = edge.node;
        // Extract numeric variant ID from GID format (e.g., "gid://shopify/ProductVariant/12345" -> "12345")
        const merchandiseId = node.merchandise?.id || '';
        const variantId = merchandiseId.includes('/') ? merchandiseId.split('/').pop() : merchandiseId;
        
        return {
          product: node.merchandise?.product?.title || 'Unknown',
          variant: node.merchandise?.title || 'Unknown',
          merchandiseId: merchandiseId,  // Full GID for reference
          variantId: variantId,           // Numeric ID for DOM matching
          quantity: node.quantity,
          price: node.cost?.amountPerQuantity?.amount,
          compareAtPrice: node.cost?.compareAtAmountPerQuantity?.amount,
          totalAmount: node.cost?.totalAmount?.amount,
          discountAllocations: node.discountAllocations || []
        };
      });
      
      const hasLineDiscounts = lineAllocations.some(line => line.discountAllocations.length > 0);
      const totalLineDiscounts = lineAllocations.reduce((sum, line) => {
        return sum + line.discountAllocations.reduce((lineSum, alloc) => {
          return lineSum + Number.parseFloat(alloc.discountedAmount?.amount || 0);
        }, 0);
      }, 0);
      
      
      return {
        success: appliedCodes.length > 0,
        cart: updatedCart,
        appliedCodes,
        notApplied,
        checkoutUrl: updatedCart.checkoutUrl,
        hasLineDiscounts,
        totalLineDiscounts,
        lineAllocations
      };
      
    } catch (error) {
      return null;
    }
  }

  /**
   * Apply multiple discount codes and redirect to checkout via Storefront API
   * This ensures all combinable codes are applied before checkout
   */
  async applyMultipleCodesAndCheckout(codes = []) {
    const result = await this.applyDiscountCodesViaStorefront(codes);
    
    if (result && result.checkoutUrl) {
      window.location.href = result.checkoutUrl;
      return true;
    }
    
    // Fallback: redirect to standard checkout
    window.location.href = '/checkout';
    return false;
  }

  /**
   * Get discount code for bundle tier (codes must be pre-created in Shopify Admin)
   * @param {Object} bundleData - Bundle information
   * @param {Object} cartData - Optional cart data with actual prices
   */
  async generateDiscountCode(bundleData, cartData = null) {
    const { bundleId, bundleTier, bundleTotal, bundlePrice, items, currency } = bundleData;
    
    // Format bundle group ID for discount code (uppercase, truncate to 20 chars)
    // Note: Matching serverless function format - dashes are preserved
    let bundleGroupId = '';
    if (bundleId) {
      bundleGroupId = bundleId
        .toString()
        .toUpperCase()
        .substring(0, 20); // Truncate to 20 characters max
    }
    
    // Fixed code per tier with bundle group ID for uniqueness (must be pre-created in Shopify Admin)
    // Format: BDL-{QUANTITY}P-{BUNDLE_GROUP_ID}
    const code = bundleGroupId 
      ? `BDL-${bundleTier}P-${bundleGroupId}`
      : `BDL-${bundleTier}P`; // Fallback if no bundleId provided
    
    // Return the code (assumes it exists in Shopify Admin)
    return code;
    
    // Calculate discount amount
    let originalTotal = 0;
    const bundleTotalCents = Math.round(bundleTotal * 100);
    
    // Method 1: Use cart data if provided (most accurate)
    if (cartData && cartData.items) {
      cartData.items.forEach(item => {
        // Check if this item is part of the bundle
        if (item.properties && item.properties._bundle_id === bundleId) {
          // Use line_price (total for this line) or calculate from price * quantity
          if (item.original_line_price) {
            originalTotal += item.original_line_price;
          } else if (item.line_price) {
            originalTotal += item.line_price;
          } else if (item.variant && item.variant.price) {
            originalTotal += item.variant.price * item.quantity;
          }
        }
      });
    } else if (items && items.length > 0) {
      // Method 2: Use item variant prices if available
      items.forEach(item => {
        if (item.variant && item.variant.price) {
          // Price is already in dollars, convert to cents
          originalTotal += Math.round(parseFloat(item.variant.price) * 100);
        } else if (item.properties && item.properties._bundle_price) {
          // Method 3: Estimate from bundle price
          // Try to get actual price by fetching variant data
          // For now, use a conservative estimate
          const estimatedOriginal = parseFloat(item.properties._bundle_price) * 1.3; // 30% markup estimate
          originalTotal += Math.round(estimatedOriginal * 100);
        }
      });
    }
    
    // Calculate discount
    const discountAmountCents = originalTotal > 0 ? (originalTotal - bundleTotalCents) : 0;
    
    // Only create discount if there's a positive discount
    if (discountAmountCents <= 0) {
      return null;
    }
    
    // Note: Discount codes must be pre-created in Shopify Admin
    // Admin API cannot be called from browser due to CORS restrictions
    // This function now only returns the code name (assumes it exists)
    
    return code;
  }

  /**
   * Find existing discount code by code string
   */
  async findExistingDiscountCode(code) {
    try {
      // Extract tier from code (supports both formats):
      // - BDL-2P (old format)
      // - BDL-2P-LESTRETCHBRA (new format with bundle group ID)
      const tierMatch = code.match(/BDL-(\d+)P(?:-([A-Z0-9-]+))?/);
      const tier = tierMatch ? tierMatch[1] : null;
      const bundleGroupId = tierMatch ? tierMatch[2] : null;
      
      if (!tier) {
        return null;
      }
      
      // Search price rules by title pattern
      const searchTitle = `Bundle Discount - ${tier}-Pack`;
      const priceRulesResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules.json?limit=250`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        }
      });

      if (!priceRulesResponse.ok) {
        return null;
      }

      const priceRulesData = await priceRulesResponse.json();
      
      if (priceRulesData.price_rules && priceRulesData.price_rules.length > 0) {
        // Find price rule with matching title
        const matchingPriceRule = priceRulesData.price_rules.find(pr => 
          pr.title && pr.title.includes(searchTitle)
        );
        
        if (matchingPriceRule) {
          // Get discount codes for this price rule
          const priceRuleId = matchingPriceRule.id;
          const codesResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}/discount_codes.json`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Access-Token': this.apiToken
            }
          });

          if (codesResponse.ok) {
            const codesData = await codesResponse.json();
            if (codesData.discount_codes && codesData.discount_codes.length > 0) {
              const matchingCode = codesData.discount_codes.find(dc => dc.code === code);
              if (matchingCode) {
                return { ...matchingCode, price_rule_id: priceRuleId };
              }
              // If code doesn't match exactly but price rule exists, return the first code
              // (in case code was manually changed)
              if (codesData.discount_codes.length > 0) {
                return { ...codesData.discount_codes[0], price_rule_id: priceRuleId };
              }
            }
          }
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get price rule by ID
   */
  async getPriceRule(priceRuleId) {
    try {
      const response = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.price_rule;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update price rule with new discount amount
   */
  async updatePriceRule(priceRuleId, discountAmount) {
    try {
      const response = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          price_rule: {
            value: `-${discountAmount}`
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Price rule update error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create new discount code and price rule
   */
  async createDiscountCode(code, bundleTier, discountAmount) {
    try {
      // Create price rule (discount)
      const priceRuleResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          price_rule: {
            title: `Bundle Discount - ${bundleTier}-Pack`,
            target_type: 'line_item',
            target_selection: 'all',
            allocation_method: 'across',
            value_type: 'fixed_amount',
            value: `-${discountAmount}`,
            customer_selection: 'all',
            starts_at: new Date().toISOString(),
            ends_at: null,
            usage_limit: null, // No limit - reusable
            once_per_customer: false,
            prerequisite_subtotal_min: null,
            prerequisite_subtotal_max: null,
            prerequisite_quantity_range: null,
            prerequisite_quantity_min: null,
            prerequisite_quantity_max: null,
            prerequisite_shipping_country_code: null,
            prerequisite_product_ids: [],
            prerequisite_variant_ids: [],
            prerequisite_collection_ids: [],
            prerequisite_customer_ids: [],
            entitlement_to_qualifying_quantity_ratio: {
              entitled_quantity: null,
              qualifying_quantity: null
            },
            prerequisite_to_entitlement_quantity_ratio: {
              prerequisite_quantity: null,
              entitled_quantity: null
            },
            prerequisite_to_entitlement_purchase: {
              prerequisite_amount: null
            }
          }
        })
      });

      if (!priceRuleResponse.ok) {
        throw new Error(`API error: ${priceRuleResponse.statusText}`);
      }

      const priceRuleData = await priceRuleResponse.json();
      const priceRuleId = priceRuleData.price_rule?.id;

      if (!priceRuleId) {
        throw new Error('Failed to get price rule ID');
      }

      // Create discount code
      const codeResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}/discount_codes.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          discount_code: {
            code: code
          }
        })
      });

      if (!codeResponse.ok) {
        throw new Error(`Code creation error: ${codeResponse.statusText}`);
      }

      const codeData = await codeResponse.json();
      return codeData.discount_code?.code || code;

    } catch (error) {
      throw error;
    }
  }

  /**
   * Apply discount code to cart
   */
  async applyDiscountCode(code) {
    try {
      let discountNotFound = false;

      // Method 1: Try using the discount endpoint
      try {
        const response = await fetch('/discount/' + encodeURIComponent(code), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          await new Promise(resolve => setTimeout(resolve, 120));
          this.refreshCart();
          return true;
        }
        if (response.status === 404) {
          discountNotFound = true;
        }
      } catch (e) {
        // Try next method
      }

      // Method 2: Use cart update with discount code
      try {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: code })
        });

        if (updateResponse.ok) {
          const data = await updateResponse.json().catch(() => ({}));
          if (!data.message || !/invalid|not found|doesn't exist/i.test(String(data.message))) {
            await new Promise(resolve => setTimeout(resolve, 200));
            this.refreshCart();
            return true;
          }
          discountNotFound = true;
        } else if (updateResponse.status === 422) {
          discountNotFound = true;
        }
      } catch (e) {
        // Try next method
      }

      // Method 3: Use cart attributes (alternative method)
      try {
        const attrResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { '_discount_code': code } })
        });
        if (attrResponse.ok) {
          this.refreshCart();
          return true;
        }
      } catch (e) {
        // Continue to fallback
      }

      // Don't redirect if discount doesn't exist - would lose currency/market context
      if (discountNotFound) {
        return false;
      }

      // Method 4: Redirect - preserve current URL path to keep market/currency (e.g. GBP)
      const url = new URL(window.location.href);
      url.searchParams.set('discount', code);
      window.location.href = url.toString();
      return true;

    } catch (error) {
      // Preserve current URL when redirecting on error
      const url = new URL(window.location.href);
      url.searchParams.set('discount', code);
      window.location.href = url.toString();
      return false;
    }
  }

  /**
   * Apply multiple discount codes to cart via Storefront API
   * Uses cartDiscountCodesUpdate mutation which supports stacking combinable discounts
   */
  async applyDiscountCodes(codes = []) {
    const codeList = Array.isArray(codes) ? codes : [codes];
    // Force uppercase for deduplication - Shopify discount codes are case-insensitive
    const normalized = Array.from(new Set(
      codeList
        .map(code => (code || '').toString().trim().toUpperCase())
        .filter(Boolean)
    ));

    if (normalized.length === 0) {
      return false;
    }

    if (normalized.length === 1) {
      return this.applyDiscountCode(normalized[0]);
    }

    // Try Storefront API first (supports multiple combinable codes)
    try {
      const key = 'bundle_storefront_discount';
      const now = Date.now();
      const cacheKey = normalized.sort().join('|');
      const data = JSON.parse(sessionStorage.getItem(key) || '{}');
      if (data.codes === cacheKey && data.ts && (now - data.ts) < 15000) {
        return false;
      }
      sessionStorage.setItem(key, JSON.stringify({ codes: cacheKey, ts: now }));
    } catch (error) {
      // ignore storage failures
    }

    const result = await this.applyDiscountCodesViaStorefront(normalized);
    
    if (result && result.success) {
      // Storefront cart is separate from Ajax cart - redirect to checkout with discounts
      if (result.checkoutUrl) {
        // Store the checkout URL for later use
        try {
          sessionStorage.setItem('bundle_storefront_checkout', JSON.stringify({
            url: result.checkoutUrl,
            codes: result.appliedCodes,
            ts: Date.now()
          }));
        } catch (e) {
          // ignore
        }
      }
      
      return true;
    }

    // Fallback: try the old redirect method (won't stack, but better than nothing)
    const combinedCodes = normalized.map(code => encodeURIComponent(code)).join(',');
    const discountUrl = `/discount/${combinedCodes}?redirect=/cart`;
    window.location.href = discountUrl;
    return true;
  }

  /**
   * Apply multiple discount codes and proceed to checkout via Storefront API
   * This creates a Storefront cart with all discounts applied and redirects to its checkout
   */
  async applyDiscountCodesForCheckout(codes = []) {
    const codeList = Array.isArray(codes) ? codes : [codes];
    // Force uppercase for deduplication - Shopify discount codes are case-insensitive
    const normalized = Array.from(new Set(
      codeList
        .map(code => (code || '').toString().trim().toUpperCase())
        .filter(Boolean)
    ));

    if (normalized.length === 0) {
      return false;
    }

    if (normalized.length === 1) {
      return this.applyDiscountCode(normalized[0]);
    }

    // Throttle to prevent loops
    try {
      const key = 'bundle_storefront_checkout_attempt';
      const now = Date.now();
      const cacheKey = normalized.sort().join('|');
      const data = JSON.parse(sessionStorage.getItem(key) || '{}');
      if (data.codes === cacheKey && data.ts && (now - data.ts) < 15000) {
        return false;
      }
      sessionStorage.setItem(key, JSON.stringify({ codes: cacheKey, ts: now }));
    } catch (error) {
      // ignore storage failures
    }

    // Use Storefront API to apply all codes and get checkout URL
    return this.applyMultipleCodesAndCheckout(normalized);
  }

  /**
   * Remove discount code from cart
   */
  async removeDiscountCode(code) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });

      if (response.ok) {
        this.refreshCart();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Refresh cart display without page reload
   */
  refreshCart() {
    // Try to use cart-items custom element's update method
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.updateCartAfterGrouping === 'function') {
      cartItems.updateCartAfterGrouping();
    } else if (cartItems && typeof cartItems.renderItems === 'function') {
      cartItems.renderItems();
    } else if (typeof window.theme !== 'undefined' && window.theme.cartDrawer) {
      window.theme.cartDrawer.refresh();
    } else if (typeof window.theme !== 'undefined' && window.theme.cart) {
      window.theme.cart.refresh();
    } else {
      // Last resort: reload page only if no other method works
      window.location.reload();
    }
  }

}

// Initialize when DOM is ready
(function() {
  'use strict';

  // Wait for DOM and theme settings
  function initBundleDiscountAPI() {
    const shopDomain = window.bundleDiscountConfig?.shopDomain || window.Shopify?.shop || 'sheer-2.myshopify.com';

    const discountAPI = new BundleDiscountAPI({
      apiToken: null,
      shopDomain: shopDomain
    });

    window.bundleDiscountAPI = discountAPI;
    return discountAPI;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleDiscountAPI);
  } else {
    initBundleDiscountAPI();
  }
})();

